import { setTimeout } from 'timers'
import { onMounted, onUnmounted, reactive, Ref, ref, UnwrapNestedRefs, watch } from 'vue'
import Scanner, { ScannerListenerType } from '@/utils/scan'
import { validateEan } from '@/utils/ean'
import { BarcodeTypeId } from '@/types/barcode'
import { Notify, QNotifyUpdateOptions, QSpinnerGears } from 'quasar'
import { ArgumentsType } from 'vitest'

const DEBUGGER_ID = 'input_listener_debugger'
const LISTENER_ID_PRE = 'input_listener_'
const DEFAULTS = {
  threshold: 200,
  event: 'keydown'
}

export enum InputScope {
  Default = 'all',
  All = 'all', // any character
  AlphaNumeric = 'alpha-numeric', // letters & numbers
  Numeric = 'numeric', // numbers
  NonNumeric = 'non-numeric', // letters & special characters
  NonNumericStrict = 'non-numeric-strict', // strictly letters only
  Special = 'special' // special characters
}

export interface TimerEventHandlers {
  start: (timer: InputState['timer'], event: Event) => void,
  end: (buffer: any, type: string, speedy: boolean, isSingle: boolean, state: UnwrapNestedRefs<InputState>) => void
}

export interface InputListenerOptions {
  listenerId: string,
  element: HTMLInputElement | Ref<HTMLInputElement | undefined>,
  inputElementId: string,
  autoCreateListener: boolean,
  createInput: boolean,
  hideInput: boolean,
  returnOn: string,
  includeReturnChar: boolean,
  notifyOnInput: boolean,
  eventName: 'keydown' | 'keyup' | 'input',
  inputScope: InputScope,
  threshold: number,
  debug: boolean
}

export interface InputState<V = any> {
  listenerId: string,
  element: Element | Ref<Element> | undefined,
  elementId: string,
  active: boolean,
  speedy: boolean, // true if input is faster than set threshold
  disabled: boolean,
  isSingle: boolean,
  checker?: number, // checker interval id
  options: Partial<InputListenerOptions>,
  currentNotify?: ReturnType<Notify['create']>,
  timer: IntervalTimer | undefined,
  fn: ((event: Event) => void) | undefined,
  type?: BarcodeTypeId & string,
  lastBufferedValue?: V
  bufferType?: string
  buffer?: V
}

function hideKeyboard (el: HTMLElement) {
  el.setAttribute('readonly', 'readonly')
  // el.setAttribute('disabled', 'true')
  setTimeout(function () {
    // el.blur()

    el.removeAttribute('readonly')
    // el.removeAttribute('disabled')
  }, 100)
}

function hideElement<T extends HTMLElement> (el: T, hide?: boolean) {
  el.style.position = 'fixed'
  el.style.top = '0px'
  el.style.right = '0px'
  el.style.width = '0'
  el.style.height = '0'
  el.style.overflow = 'hidden'
  el.style.opacity = '0'
  el.style.filter = 'alpha(opacity=0)'

  if (hide === undefined || hide === true) hideKeyboard(el)
  return el
}

function getListenerScope (scope?: InputScope): string {
  switch (scope) {

    case InputScope.AlphaNumeric:
      return ''
    case InputScope.Numeric:
      return ''
    case InputScope.NonNumeric:
      return ''
    case InputScope.NonNumericStrict:
      return ''
    case InputScope.Default:
    case InputScope.All:
    default:
      return 'keydown'
  }
}

export class IntervalTimer {
  private _active: boolean
  private _interval: number
  private _direction: 'up' | 'down'
  private _current: number
  private _callback?: () => void
  private _called: boolean

  constructor(interval: number, callback?: () => void, direction?: 'up' | 'down') {
    this._active = false
    this._interval = interval
    this._direction = direction || 'down'
    this._current = this._direction === 'down' ? this._interval : 0
    this._callback = callback
    this._called = false

  }
  // static toMiliseconds (value: string | number) {
  //   value?.length
  // }

  get interval () {
    return this._interval
  }
  set interval (i: number) {
    this._interval = i
  }

  get offset () {
    return this._interval - this._current
  }

  get active () {
    return this._active
  }

  private shouldRun () {
    return this._direction === 'down'
      ? (this._current > 0)
      : (this._current < Math.max(0, this._interval))
  }

  private isFinished () {
    return this._direction === 'down' ? this._current <= 0 : this._current >= this._interval
  }

  private run () {
    if ((!this._active || !this.shouldRun()) || this.isFinished()) {
      this._active = false
      return
    }
    setTimeout(() => {
      if (this._direction === 'down') this._current--
      else this._current++

      if (!this.isFinished()) this.run()
      else {
        this._active = false
        this.executecallback()
      }
    }, 1)

  }

  private executecallback () {
    if (typeof this._callback === 'function' && !this._called) {
      this._called = true
      this._callback()
    }
  }

  start () {
    if (this._active) return
    this._active = true
    this.run()
  }

  restart () {
    this._active = true
    this._called = false
    this._current = this._direction === 'down' ? Math.max(0, this._interval) : 0
    this.run()
  }

  pause () {
    this._active = false
    return this.offset
  }

  abort () {
    this._active = false
    this._current = this._direction === 'down' ? 0 : Math.max(0, this._interval)
  }

  finish () {
    this.abort()
    this.executecallback()
  }
}

function injectDebugOverlay (state, options) {
  if (!options?.debug || process.env.NODE_ENV === 'production') return

  const existing = window.document.getElementById(DEBUGGER_ID)
  if (existing) window.document.body.removeChild(existing)
  const debugEl: HTMLElement = window.document.createElement('div')
  const debuggerWidth = '90vw'
  debugEl.id = DEBUGGER_ID
  debugEl.style.position = 'fixed'
  debugEl.style.zIndex = '99999'
  debugEl.style.padding = '15px'
  debugEl.style.top = '20px'
  debugEl.style.right = '5vw'
  debugEl.style.width = '30px'
  debugEl.style.maxWidth = '320vw'
  debugEl.style.height = '30px'
  debugEl.style.maxHeight = '50vh'
  debugEl.style.overflow = 'hidden'
  debugEl.style.backgroundColor = '#081137'
  debugEl.style.color = '#081137'
  debugEl.style.opacity = '0.3'
  debugEl.style.fontFamily = 'Mono'
  debugEl.style.borderRadius = '100px'
  debugEl.innerHTML = `
    <div>type    | ${state.type}</div>
    <div>element    | ${(state.element as HTMLElement)?.id}</div>
    <div>active     | ${state.active}</div>
    <div>speedy     | ${state.speedy}</div>
    <div>isSingle   | ${state.isSingle}</div>
    <div>timer      | ${state.timer?.offset}</div>
    <div>lastBufferedValue     | ${state.lastBufferedValue}</div>
    <div>bufferType | ${state.bufferType}</div>
    <div>validation     | ${state.bufferType?.includes('EAN') ? JSON.stringify(validateEan(state.buffer)) : ''}</div>
    <div>bufferLength     | ${typeof state.buffer === 'object' ? JSON.stringify(state.buffer).length : state.buffer?.length}</div>
    <div>buffer     | ${typeof state.buffer === 'object' ? JSON.stringify(state.buffer) : state.buffer}</div>
  `
  debugEl.addEventListener('click', function (e) {
    // console.log(debugEl.style.width)
    if (debugEl.style.width === debuggerWidth) {
      debugEl.style.width = '30px'
      debugEl.style.maxHeight = '30px'
      debugEl.style.borderRadius = '100px'
      debugEl.style.overflow = 'hidden'
      debugEl.style.color = '#081137'
      // debugEl.style.borderRadius = '30px'
    } else {
      debugEl.style.width = debuggerWidth
      debugEl.style.height = '320px'
      debugEl.style.maxHeight = '50vh'
      debugEl.style.borderRadius = '6px'
      debugEl.style.overflow = 'auto'
      debugEl.style.color = '#f9f9f9'
      debugEl.style.opacity = '1'
      // debugEl.style.borderRadius = '6px'
    }
  })
  window.document.body.appendChild(debugEl)
}

function createDeviceListener (state, actions?: TimerEventHandlers, options?: Partial<InputListenerOptions>) {
  state.type = Scanner.current
  return Scanner.device?.listen(ScannerListenerType.Scan,
    value => {
      state.speedy = true
      state.isSingle = true
      state.bufferType = value.type
      state.buffer = String(value.data).trim() || value

      if (!state.disabled) actions?.end(state.buffer, state.bufferType, state.speedy, state.isSingle, state)
    },
    e => {
      console.error(e)
    }
  )
}

function createFocusChecker (state, options) {
  if (state.checker !== undefined) window.clearInterval(state.checker)
  state.checker = window.setInterval(() => {
    if (!state.element) window.clearInterval(state.checker)
    else if (shouldRefocus(state)) refocus(state, options)
  }, 1000)

  return state.checker
}

function isFocussed (state) {
  return document.activeElement && document.activeElement.id === (state.element as HTMLInputElement).id
}

function shouldRefocus (state) {
  if (state.disabled) return false

  if (!isFocussed(state)) {
    // refocus condition - active element is:
    // - body
    // - not an input, or select
    const willRefocus = (document.activeElement?.nodeName !== 'INPUT' && document.activeElement?.nodeName !== 'SELECT' && document.activeElement?.nodeName !== 'TEXTAREA') ||
      document.activeElement === document.body

    // if (willRefocus) console.log('refocus')

    return willRefocus
  }

  return false
}

function refocus (state, options) {
  (state.element as HTMLInputElement).focus()
  if (options?.hideInput === undefined || options?.hideInput === true) hideKeyboard(state.element as HTMLElement)
}

function createGenericInputListener (state, actions, options) {
  state.type = 'Generic'
  const existingEl: Element | null = state.element || state.elementId ? window.document.getElementById(state.elementId) : null
  if (existingEl) {
    // NOTE: doesn't remove the element if a custom target element was provided
    existingEl.removeEventListener(options?.eventName || state.element instanceof HTMLInputElement ? 'input' : DEFAULTS.event, state.fn)
    if (existingEl.id && !options?.inputElementId) existingEl.remove()
  }

  // create a DOM input node to listen for input events
  // set styles to visually hide the element, while remaining focussable
  const inputEl = state.elementId ? window.document.getElementById(state.elementId) : window.document.createElement('input')
  if (!inputEl) return console.error(state.elementId ? `[InputListener] Could not match element by id "${state.elementId}"` : 'InputListener: Failed to create input element')

  const el = hideElement(inputEl, options?.hideInput)

  el.id = state.elementId || `${LISTENER_ID_PRE}${state.listenerId}__${state.type}`

  state.element = state.element || state.elementId ? el : (options?.createInput ? window.document.body.appendChild(el) : window)

  if (!state.element) return console.error('[InputListener] Could not create input listener. state.element was undefined')

  // create the state buffer timer
  state.timer = new IntervalTimer(state.options.threshold || DEFAULTS.threshold, () => {
    if (actions?.end) actions.end(state.buffer, state.bufferType, state.speedy, state.isSingle, state)
    state.lastBufferedValue = state.buffer
    state.bufferType = undefined
    state.buffer = undefined
    if (state.element instanceof HTMLInputElement) state.element.value = ''
    state.active = false
    state.isSingle = false
    state.speedy = false
  })

  const listener = state.element.addEventListener(options?.eventName || state.element instanceof HTMLInputElement ? 'input' : DEFAULTS.event, state.fn)

  if ((state.element as HTMLInputElement).focus) (state.element as HTMLInputElement).focus()

  // create a checker for current document focus. if nothing else if focussed, re-focus the hidden input
  if (state.element instanceof HTMLInputElement) createFocusChecker(state, options)

  return listener
}

// used to manually re-assign the target input for a generic input listener
// sets options.inputElementId tot the id of the given element
function assignTarget (e: string | Element, state) {
  if (e instanceof Element) {
    state.elementId = e.id
    state.element = e
    return
  }
  const element = window.document.getElementById(e)
  if (!element) {
    console.error(`Could not match element by id "${e}"`)
    return
  }
  state.elementId = element.id
  state.element = element
}

function createListener (state: UnwrapNestedRefs<InputState>, actions?: TimerEventHandlers, options?: Partial<InputListenerOptions>) {
  // console.log('Creating listener', state.listenerId, state.elementId)
  if (!state.fn) {
    console.warn('useInputListener: Could not create event listener. No listener handler function was defined')
    return
  }

  // if the scanner device is not yet set, create a generic listener, and when the scanner return a device, replace it
  const listener = Scanner.device
    ? createDeviceListener(state, actions, options)
    : createGenericInputListener(state, actions, options)

  Scanner.getDevice().then(device => {
    if (device) {
      destroyListener(state, options)
      createDeviceListener(state, actions, options)
    }
  }).catch(console.error)

  injectDebugOverlay(state, options)
  watch(() => state.buffer, e => injectDebugOverlay(state, options))

  return listener
}

function destroyListener (state: UnwrapNestedRefs<InputState>, options) {
  // console.log('Destroying listener', state.listenerId, state.elementId)
  try {
    if (!state.fn) {
      console.warn('useInputListener: Could not remove event listener. No listener handler function was defined')
      return
    }

    if (state.element) {
      state.element?.removeEventListener(options?.eventName || DEFAULTS.event, state.fn)
      if (!options.inputElementId) state.element.remove()
    }
    const debugEl = window.document.getElementById(DEBUGGER_ID)
    if (debugEl) window.document.body.removeChild(debugEl)
  } catch (e) {
    console.warn('Error destroying listener', e)
  }
}

function getListenerHandler (eventHandlers: TimerEventHandlers, state: UnwrapNestedRefs<InputState>, options?: Partial<InputListenerOptions>): (e: Event) => void {
  return (e: Event) => {
    const event = e as InputEvent
    if (!state.timer) {
      console.warn('useInputListener: Cannot start input timer. No IntervalTimer was set')
      return
    }

    const inputValue = (state.element as HTMLInputElement).value
    if (state.timer.active) {
      state.isSingle = false
      // console.log('active', state.timer.offset)
      state.buffer = e instanceof InputEvent ? inputValue : `${(state.buffer ?? '')}${(e as KeyboardEvent).key}`
      if (state.timer.offset) state.speedy = true
      state.timer.restart()
    } else {
      // console.log('starting event', e)
      state.buffer = e instanceof InputEvent ? inputValue : `${state.buffer ?? ''}${(e as KeyboardEvent).key}`
      state.active = true
      state.isSingle = true
      if (eventHandlers?.start) eventHandlers?.start((state as InputState).timer, e)
      state.timer.restart()
    }
  }
}

function createDefaultEventHandlers (targetRef: Ref<string>): TimerEventHandlers {
  return {
    start: (e, s) => {
      //
    },
    end: (e, type, isScanner, isSingle, s) => {
      // console.debug('end', e)
      createNotification(e, type as BarcodeTypeId, isScanner, isSingle, s as InputState)
      if (isScanner || isSingle && e?.length > 1) targetRef.value = e
    }
  }
}

function createNotification (e: string, type: 'error' | 'warn' | BarcodeTypeId | null, isScanner: boolean, isSingle: boolean, state: InputState, pending?: boolean) {
  if (state.options?.notifyOnInput !== false) {
    let classes = 'custom-notify scanner-notify '
    if (type === 'error') classes += 'scanner-notify--error'
    if (type === 'warn') classes += 'scanner-notify--warn'
    state.currentNotify = Notify.create({ message: e, position: 'top', progress: true, group: false, timeout: 4000, classes, spinner: pending ? QSpinnerGears : false })
  }
}

function updateNotification (e: string, type: 'error' | 'warn' | BarcodeTypeId | null, state: InputState, pending?: boolean) {
  // console.debug('update notification',e, type)
  if (!state.currentNotify) return createNotification(e, type || null, true, true, state, pending)

  if (state.options?.notifyOnInput !== false) {
    let classes = 'custom-notify scanner-notify '
    if (type === 'error') classes += 'scanner-notify--error'
    if (type === 'warn') classes += 'scanner-notify--warn'

    const notifyData: QNotifyUpdateOptions = { message: e, classes, spinner: pending ? QSpinnerGears : false }
    if (pending) notifyData.timeout = 2000
    state.currentNotify(notifyData)
  }
}

export default function useInputListener (eventHandlers: TimerEventHandlers | Ref<string | undefined>, opts?: Partial<InputListenerOptions>) {
  const options = opts || { createInput: true, debug: process.env.NODE_ENV !== 'production' }

  const state = reactive<InputState>({
    // listenerId: Date.now().toString(16),
    listenerId: options.listenerId || '0', // if you want multiple listeners of the same type you will have to provide an id option
    element: options.element ? options.element instanceof ref ? (options.element as Ref<HTMLInputElement>) : options.element as HTMLInputElement : undefined, // optional element instead of "window"
    elementId: '',
    options: options || {},
    currentNotify: undefined,
    checker: undefined,
    active: false,
    speedy: false,
    disabled: false,
    isSingle: false,
    type: undefined,
    fn: undefined,
    timer: undefined,
    lastBufferedValue: undefined,
    bufferType: undefined,
    buffer: undefined // starts filling every time the time between inputs is below the set threshold option
  })

  state.elementId = state.element?.id || options?.inputElementId || ''

  function disableInput (state: UnwrapNestedRefs<InputState>, options?: Partial<InputListenerOptions>) {
    // if this is a generic listener, this makes shouldRefocus return false
    state.disabled = true

    // if this is a device api listener, call the device api mute() method
    if (Scanner.device?.mute) Scanner.device.mute(ScannerListenerType.Scan)
  }

  function enableInput (state: UnwrapNestedRefs<InputState>, options?: Partial<InputListenerOptions>) {
    state.disabled = false

    if (Scanner.device?.listen) Scanner.device.listen(ScannerListenerType.Scan)
  }

  const handlers = reactive(
    'value' in eventHandlers
      ? createDefaultEventHandlers(eventHandlers as Ref<string>)
      : eventHandlers
  )

  const createListenerControllers = () => ({
    create: () => createListener(state, handlers, options),
    destroy: () => destroyListener(state, options)
  })

  const listener = reactive(createListenerControllers())
  const resetListenerControllers = (e: HTMLInputElement | string) => {
    destroyListener(state, options)

    assignTarget(e, state)

    const ctrls = createListenerControllers()
    listener.create = ctrls.create
    listener.destroy = ctrls.destroy

    listener.create()
  }

  // scoped input event listener callback handler
  state.fn = getListenerHandler(handlers, state, options)

  function setStartHandler (handler: TimerEventHandlers['start']) {
    handlers.start = handler
  }

  function setEndHandler (handler: TimerEventHandlers['end']) {
    handlers.end = handler
  }

  if (options.autoCreateListener) listener.create()

  // standard mounted hooks
  onMounted(() => {
    if (options.autoCreateListener) listener.create()
  })
  onUnmounted(() => {
    if (options.autoCreateListener) listener.destroy()
  })

  return {
    state,
    handlers,
    listener,
    notify: (input: string, type?: 'error' | 'warn' | BarcodeTypeId | null, isScanner?: boolean, isSingle?: boolean, st: InputState = state as InputState) => createNotification(input, type || null, isScanner || true, isSingle || true, st),
    updateNotification: (input: string, type?: 'error' | 'warn' | BarcodeTypeId | null, pending?: boolean) => {
      if (!state.currentNotify) return createNotification(input, type || null, true, true, state as InputState, pending)
      return updateNotification(input, type || null, state as InputState, pending)
    },
    focus: () => refocus(state, options),
    isSpeedy: (b: boolean) => state.speedy = b,
    disable: () => disableInput(state, options),
    enable: () => enableInput(state, options),
    hideElement,
    setTarget: resetListenerControllers,
    setHandler: (type, fn) => {
      switch (type) {
        case 'start':
          return setStartHandler(fn)
        case 'end':
          return setEndHandler(fn)
        default:
          return false
      }
    }
  }
}
